import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import type { BulkSearch } from '../../types/search';

export interface Tag {
  id: string;
  projectId: string;
  name: string;
  color: string;
}

export function useTagSuggestions(projectId: string, query: string) {
  return useQuery<Tag[]>({
    queryKey: ['tagSuggestions', projectId, query],
    queryFn: () => api.get<Tag[]>(`/project/${projectId}/tag/all/suggest?query=${encodeURIComponent(query)}`),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useTagsByIds(projectId: string, ids: string[]) {
  return useQuery<Tag[]>({
    queryKey: ['tagsByIds', projectId, ids],
    queryFn: () => api.get<Tag[]>(`/project/${projectId}/tags/${ids.join(',')}`),
    enabled: !!projectId && ids.length > 0,
  });
}

/**
 * Tag suggestions for the bulk Add-Tag control. Uses the /tag/suggest endpoint with
 * categoryBitmask=4 (project tags only — you can't manually apply system/domain tags),
 * matching Angular's addTag GetSuggestions for a resource/filter selection.
 */
export function useAddTagSuggestions(projectId: string, query: string) {
  return useQuery<Tag[]>({
    queryKey: ['addTagSuggestions', projectId, query],
    queryFn: () => {
      // The server requires all of these params (checkInverse/hasInverse/isChild/
      // includeSubTags are non-optional Booleans); mirror Angular's GetSuggestions
      // defaults. categoryBitmask=4 = project tags only.
      const params = new URLSearchParams({
        tagName: query,
        categoryBitmask: '4',
        limit: '15',
        checkInverse: 'false',
        hasInverse: 'false',
        isChild: 'false',
        includeSubTags: 'false',
      });
      return api.get<Tag[]>(`/project/${projectId}/tag/suggest?${params.toString()}`);
    },
    enabled: !!projectId && query.length > 0,
    staleTime: 30_000,
  });
}

export interface TagResourcesInput {
  name: string;
  tagRelated: boolean;
  resources: string[];
}

/** Bulk-tag an explicit set of resources (selection mode 'some'). */
export function useTagResources(projectId: string) {
  return useToastMutation<unknown, TagResourcesInput>(
    ['tags', projectId],
    (input) => api.post(`/project/${projectId}/resources/tag`, input),
    {
      success: (_d, v) => ({
        title: 'Tag Added',
        description: `${v.name} to ${v.resources.length} selection${v.resources.length === 1 ? '' : 's'}`,
        timeout: 2500,
      }),
    }
  );
}

export interface TagCollectionsInput {
  name: string;
  tagRelated: boolean;
  filter: unknown;
  columnFilters?: unknown;
}

/**
 * Bulk-tag every resource matching the filter across the given collections (selection
 * mode 'all'). Server-side + async — completion arrives via the tagging Feed event.
 */
export function useTagCollections(projectId: string, collectionIds: string[]) {
  const cids = collectionIds.join(',');
  return useToastMutation<unknown, TagCollectionsInput>(
    ['tags', projectId],
    (input) =>
      api.post(`/project/${projectId}/collection/${cids}/tag`, {
        filter: input.filter,
        columnFilters: input.columnFilters ?? null,
        name: input.name,
        tagRelated: input.tagRelated,
      }),
    {
      success: (_d, v) => ({
        title: 'Tagging Started',
        description: `${v.name} to results. We'll notify you when it completes.`,
        timeout: 2500,
      }),
    }
  );
}

export interface TagSearchInput {
  name: string;
  tagRelated: boolean;
  search: BulkSearch;
  columnFilters?: unknown;
}

/**
 * Bulk-tag every resource matching the current search (selection mode 'all' on the
 * search-results grid). Ports TagService.TagCollectionSearch / TagProjectSearch: a
 * basic search travels as the `q` query param with a null body `searchExpression`; an
 * advanced search sends an empty `q` and the expression in the body. Collection-scoped
 * when `collectionId` is given, project-wide otherwise.
 */
export function useTagSearch(projectId: string, collectionId?: string) {
  const path = collectionId
    ? `/project/${projectId}/collection/${collectionId}/tag/search`
    : `/project/${projectId}/tag/search`;

  return useToastMutation<unknown, TagSearchInput>(
    ['tags', projectId],
    (input) => {
      const q = input.search.advanced ? '' : input.search.query;
      return api.post(`${path}?q=${encodeURIComponent(q)}`, {
        name: input.name,
        tagRelated: input.tagRelated,
        searchExpression: input.search.advanced ? input.search.expression : null,
        columnFilters: input.columnFilters ?? null,
      });
    },
    {
      success: (_d, v) => ({
        title: 'Tagging Started',
        description: `${v.name} to ${collectionId ? 'Collection' : 'Project'} search results. We'll notify you when it completes.`,
        timeout: 2500,
      }),
    }
  );
}
